import { useEffect, useRef } from "react";

import { BatchUpsertInventoryValueListInput, Inventory } from "@/graphql/generated/graphql";
import { inventoryWebSocket } from "@/services/inventory-websocket.service";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { applyOptimisticUpsert } from "./apply-optimistic-upsert";
import { batchUpsertInventoryValueListMutationDocument } from "./inventory-value.gql";

// Local alias for the optimistic-update working list element. `Inventory.inventoryValueList`
// is `Array<InventoryValue>` from the generated GraphQL types; the helper works generically
// against any shape that satisfies `InventoryValueEntry`, and `InventoryValue` does.
type InventoryValueListEntry = Inventory["inventoryValueList"][number];

export type UseUpsertInventoryValueListProps = {
  onCompleted?: (data: Inventory | null, isAsync: boolean) => void;
  onError?: (e: Error) => void;
};

export const useUpsertInventoryValueList = ({ onCompleted, onError }: UseUpsertInventoryValueListProps = {}) => {
  const queryClient = useQueryClient();
  const fallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutJustSetRef = useRef<boolean>(false);

  const mutation = useMutation({
    mutationFn: async (input: BatchUpsertInventoryValueListInput) => {
      const client = createOperationClient("batchUpsertInventoryValueList");
      const data = await client.request(batchUpsertInventoryValueListMutationDocument, { input });
      return data.batchUpsertInventoryValueList as {
        status: "accepted" | "completed";
        message: string;
        inventoryId: string;
        inventory?: Inventory | null;
      };
    },
    onMutate: async (input) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.inventories.all });
      await queryClient.cancelQueries({ queryKey: queryKeys.inventoryValues.all });

      // Get the inventory ID from the first item (they should all be for the same inventory)
      const inventoryId = input.upsertInventoryValueInputList[0]?.inventoryId;
      // Snapshot the previous values
      const previousInventory = inventoryId
        ? queryClient.getQueryData(queryKeys.inventories.detail(inventoryId))
        : null;
      const previousInventoryValues = queryClient.getQueriesData({ queryKey: queryKeys.inventoryValues.all });

      // Optimistically update inventory values in cache
      input.upsertInventoryValueInputList.forEach((inventoryValue) => {
        if (inventoryValue.id) {
          queryClient.setQueryData(queryKeys.inventoryValues.detail(inventoryValue.id), (old: unknown) => ({
            ...(old as Record<string, unknown>),
            ...inventoryValue,
            updatedAt: new Date().toISOString(),
          }));
        }
      });

      // Optimistically update the inventory's inventoryValueList so cells show the new value immediately.
      // applyOptimisticUpsert dedupes by cell coordinates: a CREATE for a cell that already has a pending
      // entry (temp or real) merges into that entry instead of pushing a duplicate row. Pushing duplicates
      // used to corrupt cellStore lookups because the WS reconciler only maps the first temp-id to a real
      // one — leftovers were stranded and stale-first-match wins broke cell display until reload.
      if (inventoryId && previousInventory) {
        const currentInventory = previousInventory as Inventory;
        const nowIso = new Date().toISOString();
        const initialList: InventoryValueListEntry[] = [...(currentInventory.inventoryValueList ?? [])];
        const updatedInventoryValueList = input.upsertInventoryValueInputList.reduce<InventoryValueListEntry[]>(
          (list, inventoryValue) =>
            applyOptimisticUpsert(list, inventoryValue, inventoryId, nowIso, () => `temp-${Date.now()}-${Math.random()}`),
          initialList,
        );

        queryClient.setQueryData(queryKeys.inventories.detail(inventoryId), {
          ...currentInventory,
          inventoryValueList: updatedInventoryValueList,
        });
      }

      return { previousInventory, previousInventoryValues, inventoryId };
    },
    onError: (err, _input, context) => {
      // Clear any pending timeout on error
      timeoutJustSetRef.current = false;
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
      // Also cancel in WebSocket service if we have the inventoryId
      if (context?.inventoryId) {
        inventoryWebSocket.cancelFallbackTimeout(context.inventoryId);
      }

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousInventory && context?.inventoryId) {
        queryClient.setQueryData(queryKeys.inventories.detail(context.inventoryId), context.previousInventory);
      }
      if (context?.previousInventoryValues) {
        context.previousInventoryValues.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (onError) {
        onError(err);
      }
    },
    onSuccess: (response, _input, context) => {
      const inventoryId = response.inventoryId || context?.inventoryId;

      // If the response includes the inventory with updated values, update the cache immediately
      // This is important for emission factors and other direct inventory values that don't need recomputation
      if (response.inventory && inventoryId) {
        queryClient.setQueryData(queryKeys.inventories.detail(inventoryId), response.inventory);
        // Track this cache update so WebSocket handler can debounce if it also fires
        inventoryWebSocket.trackInvalidation(inventoryId);
        // Invalidate the inventory list to update biskoCompliance status in the table
        if (response.inventory.workspaceId) {
          // Invalidate both minimal and full queries
          queryClient.invalidateQueries({
            queryKey: queryKeys.inventories.list(response.inventory.workspaceId, true),
            refetchType: "active",
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.inventories.list(response.inventory.workspaceId, false),
            refetchType: "active",
          });
        }
      } else if (inventoryId) {
        // If inventory wasn't returned, only invalidate immediately if status is "completed"
        // If status is "accepted", wait for WebSocket update to avoid duplicate refetches
        if (response.status === "completed") {
          // Synchronous completion - invalidate immediately
          // Track this invalidation so WebSocket handler can debounce if it also fires
          inventoryWebSocket.trackInvalidation(inventoryId);
          queryClient.invalidateQueries({
            queryKey: queryKeys.inventories.detail(inventoryId),
            refetchType: "active",
          });
        }
        // Always invalidate the list to update biskoCompliance status
        const inventory = queryClient.getQueryData<Inventory>(queryKeys.inventories.detail(inventoryId));
        if (inventory?.workspaceId) {
          // Invalidate both minimal and full queries
          queryClient.invalidateQueries({
            queryKey: queryKeys.inventories.list(inventory.workspaceId, true),
            refetchType: "active",
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.inventories.list(inventory.workspaceId, false),
            refetchType: "active",
          });
        }
      }

      // Notify consumers of completion with inventory data and async status
      if (onCompleted) {
        const inventoryData =
          response.inventory ||
          (inventoryId ? queryClient.getQueryData<Inventory>(queryKeys.inventories.detail(inventoryId)) : null);
        const isAsync = response.status === "accepted";
        onCompleted(inventoryData || null, isAsync);
      }

      // Clear any existing timeout before setting a new one
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }

      // Ensure WebSocket connection for this inventory so we receive recomputation updates
      // This is critical for async recomputation - the user might not be in the room when it completes
      if (inventoryId && response.status === "accepted") {
        // Get workspaceId from inventory query to connect to inventory room
        const inventory = queryClient.getQueryData<Inventory>(queryKeys.inventories.detail(inventoryId));
        if (inventory?.workspaceId) {
          inventoryWebSocket.connect(inventory.workspaceId, inventoryId);
        }

        // Set up a fallback: if no WebSocket update is received within 10 seconds, refetch
        // This ensures users get updates even if they're not in the room when recomputation completes
        timeoutJustSetRef.current = true;
        fallbackTimeoutRef.current = setTimeout(() => {
          const queryState = queryClient.getQueryState(queryKeys.inventories.detail(inventoryId));
          // Only refetch if query is still active (user is still on the page)
          if (queryState && queryState.data) {
            queryClient.refetchQueries({
              queryKey: queryKeys.inventories.detail(inventoryId),
              type: "active",
            });
          }
          fallbackTimeoutRef.current = null;
          timeoutJustSetRef.current = false;
        }, 10000); // 10 seconds should be enough for most recomputations
        // Register the timeout so it can be cancelled when WebSocket update is received
        if (fallbackTimeoutRef.current) {
          inventoryWebSocket.registerFallbackTimeout(inventoryId, fallbackTimeoutRef.current);
        }
      }
    },
    onSettled: (_data, _error, _variables, context) => {
      // Clear timeout on settlement if it wasn't just set (safety net for edge cases)
      // This covers mutation failures where onError might not have cleared it
      if (timeoutJustSetRef.current) {
        // Timeout was just set in onSuccess, don't clear it, but reset flag for next mutation
        timeoutJustSetRef.current = false;
      } else if (fallbackTimeoutRef.current) {
        // Timeout exists but wasn't just set, clear it (safety net for edge cases)
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
        // Also cancel in WebSocket service if we have the inventoryId
        if (context?.inventoryId) {
          inventoryWebSocket.cancelFallbackTimeout(context.inventoryId);
        }
      }
    },
  });

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    upsertInventoryValueList: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
